import { toDateString } from "~/libs";

export type NavigationItemConfig = {
  href?: string;
  hrefPatterns?: string[];
  lists?: Array<{ href: string; label: string; isOwnerOnly?: boolean }>;
};

/**
 * Member用のnavigation設定
 *
 * @param defaultBusinessSectionId - デフォルトの事業セクションID
 * @returns navigationConfig
 */
export const getMemberNavigationConfig = (
  defaultBusinessSectionId: string,
): Record<string, NavigationItemConfig> => ({
  メモログ: {
    href: "/insight_posts",
    hrefPatterns: ["/", "/insight_posts", "/insight_posts/*"],
  },
  チェックアウト: {
    href: `/input_employee_surveys?business_section_id=${defaultBusinessSectionId}`,
    hrefPatterns: ["/input_employee_surveys", "/input_employee_surveys/*"],
  },
  コンディション: {
    href: `/input_employee_condition_surveys?business_section_id=${defaultBusinessSectionId}`,
    hrefPatterns: [
      "/input_employee_condition_surveys",
      "/input_employee_condition_surveys/*",
    ],
  },
  ナレッジシェア: {
    href: "/knowledge_post/team",
    hrefPatterns: ["/knowledge_post/team"],
  },
  本部の発信: {
    href: "/knowledge_post/owner",
    hrefPatterns: ["/knowledge_post/owner"],
  },
  部署の発信: {
    href: "/knowledge_post/manager",
    hrefPatterns: ["/knowledge_post/manager"],
  },
  ライブラリ: {
    href: `/document_file_category/owner?business_section_id=${defaultBusinessSectionId}`,
    hrefPatterns: ["/document_file_category/*"],
  },
  スキルマップ: {
    href: `/skill_evaluation_standard_summary_categories?business_section_id=${defaultBusinessSectionId}`,
    hrefPatterns: [
      "/skill_evaluation_standard_summary_categories",
      "/skill_evaluation_standard_summary_categories/*",
    ],
  },
  ブログ: {
    href: `/sales_concept_category/owner?business_section_id=${defaultBusinessSectionId}`,
    hrefPatterns: ["/sales_concept_category/*", "/sales_concepts/*"],
  },
  "Q&A": {
    href: `/question_answer_category/owner?business_section_id=${defaultBusinessSectionId}`,
    hrefPatterns: ["/question_answer_category/*"],
  },
  "1on1・MTG": {
    href: "/meeting_notes/new",
    hrefPatterns: ["/meeting_notes", "/meeting_notes/*"],
  },
  "KPI・KGI": {
    href: `/kpi_daily_performances/${toDateString(new Date())}?business_section_id=${defaultBusinessSectionId}`,
    hrefPatterns: [
      "/kpi_daily_performances/*",
      "/kpi_terms",
      "/kpi_terms/*",
      "/term_end_kpi_terms/*",
      "/kpi_business_section_dashboard",
    ],
  },
  ロールプレイング: {
    href: "/roleplaying_conditions/new",
    hrefPatterns: ["/roleplaying_conditions", "/roleplaying_conditions/*"],
  },
  "改善・相談": {
    href: "/kaizen_ideas",
    hrefPatterns: [
      "/kaizen_ideas",
      "/kaizen_ideas/*",
      "/kaizen_idea_replies/*",
    ],
  },
  キャンパス: {
    href: "/private_personal_memos",
    hrefPatterns: ["/private_personal_memos"],
  },
});

/**
 * Manager用のnavigation設定
 *
 * @param defaultBusinessSectionId - デフォルトの事業セクションID
 * @returns navigationConfig
 */
export const getManagerNavigationConfig = (
  defaultBusinessSectionId: string,
): Record<string, NavigationItemConfig> => ({
  メモログ確認: {
    href: "/managers/feedback/insight_posts",
    hrefPatterns: ["/managers/feedback/insight_posts", "/managers"],
  },
  メンバーメモ: {
    href: `/managers/business_section_reviews?business_section_id=${defaultBusinessSectionId}`,
    hrefPatterns: [
      "/managers/business_section_reviews",
      "/managers/business_sections/*/business_section_competency_reviews",
      "/managers/business_sections/*/business_section_memo_reviews",
      "/managers/employees/*/employee_memo_reviews",
      "/managers/employees/*/employee_competency_reviews",
    ],
  },
  チャレンジ: { lists: [] },
  チェックアウト確認: {
    href: "/managers/employee_survey_submissions",
    hrefPatterns: ["/managers/employee_survey_submissions"],
  },
  チェックアウト: {
    lists: [
      { href: "/managers/employee_surveys/new", label: "新規作成" },
      { href: "/managers/employee_surveys", label: "一覧" },
    ],
    hrefPatterns: [
      "/managers/employee_surveys",
      "/managers/employee_surveys/*",
    ],
  },
  コンディション確認: {
    href: "/managers/employee_condition_submissions",
    hrefPatterns: ["/managers/employee_condition_submissions"],
  },
  コンディション: {
    lists: [
      {
        href: "/managers/employee_condition_surveys/new",
        label: "新規作成",
        isOwnerOnly: true,
      },
      { href: "/managers/employee_condition_surveys", label: "一覧" },
    ],
    hrefPatterns: [
      "/managers/employee_condition_surveys",
      "/managers/employee_condition_surveys/*",
    ],
  },
  ナレッジシェア: {
    href: "/managers/knowledge_post/team",
    hrefPatterns: ["/managers/knowledge_post/team"],
  },
  本部の発信: {
    href: "/managers/knowledge_post/owner",
    hrefPatterns: ["/managers/knowledge_post/owner"],
  },
  部署の発信: {
    href: "/managers/knowledge_post/manager",
    hrefPatterns: ["/managers/knowledge_post/*"],
  },
  メモログ目標: {
    lists: [
      { href: "/managers/insight_post_objectives/new", label: "新規作成" },
      { href: "/managers/insight_post_objectives", label: "一覧" },
    ],
    hrefPatterns: [
      "/managers/insight_post_objectives",
      "/managers/insight_post_objectives/*",
      "/managers/belonging_employees/insight_post_objectives",
    ],
  },
  ライブラリ: {
    lists: [
      { href: "/managers/document_file_categories/new", label: "新規作成" },
      { href: "/managers/document_file_categories", label: "一覧" },
      {
        href: "/managers/document_file_category/display_orders",
        label: "並び替え",
      },
    ],
    hrefPatterns: [
      "/managers/document_file_categories",
      "/managers/document_file_categories/*",
      "/managers/document_file_category/display_orders",
    ],
  },
  スキルマップ: {
    lists: [
      {
        href: "/managers/skill_evaluation_standard_summary_categories/new",
        label: "新規作成",
      },
      {
        href: "/managers/skill_evaluation_standard_summary_categories",
        label: "一覧",
      },
      {
        href: "/managers/skill_evaluation_standard_summary_category/display_orders",
        label: "並び替え",
      },
    ],
    hrefPatterns: [
      "/managers/skill_evaluation_standard_summary_categories",
      "/managers/skill_evaluation_standard_summary_categories/*",
      "/managers/skill_evaluation_standard_summary_category/display_orders",
    ],
  },
  スキルチェック: {
    lists: [
      {
        href: "/managers/skill_evaluation_terms/new",
        label: "新規作成",
      },
      {
        href: "/managers/skill_evaluation_terms",
        label: "一覧",
      },
    ],
    hrefPatterns: [
      "/managers/skill_evaluation_terms",
      "/managers/skill_evaluation_terms/*",
      "/managers/belonging_employees/skill_evaluation_terms",
    ],
  },
  ブログ: {
    lists: [
      { href: "/managers/sales_concept_categories/new", label: "新規作成" },
      { href: "/managers/sales_concept_categories", label: "一覧" },
      {
        href: "/managers/sales_concept_category/display_orders",
        label: "並び替え",
      },
    ],
    hrefPatterns: [
      "/managers/sales_concept_categories",
      "/managers/sales_concept_categories/*",
      "/managers/sales_concept_category/display_orders",
    ],
  },
  "Q&A": {
    lists: [
      { href: "/managers/question_answer_categories/new", label: "新規作成" },
      { href: "/managers/question_answer_categories", label: "一覧" },
      {
        href: "/managers/question_answer_category/display_orders",
        label: "並び替え",
      },
      {
        href: "/managers/private_question_answers",
        label: "メンバーの投稿",
      },
    ],
    hrefPatterns: [
      "/managers/question_answer_categories",
      "/managers/question_answer_categories/*",
      "/managers/question_answer_category/display_orders",
      "/managers/private_question_answers",
      "/managers/belonging_employees/private_question_answers",
    ],
  },
  "1on1・MTGテンプレ": {
    lists: [
      { href: "/managers/meeting_note_templates/new", label: "新規作成" },
      { href: "/managers/meeting_note_templates", label: "一覧" },
    ],
    hrefPatterns: [
      "/managers/meeting_note_templates",
      "/managers/meeting_note_templates/*",
    ],
  },
  "1on1・MTG": {
    lists: [
      { href: "/managers/meeting_notes/new", label: "新規作成" },
      { href: "/managers/meeting_notes", label: "一覧" },
    ],
    hrefPatterns: [
      "/managers/meeting_notes",
      "/managers/meeting_notes/*",
      "/managers/belonging_employees/meeting_notes",
    ],
  },
  "KPI・KGI": {
    lists: [
      { href: "/managers/kpi_terms/new", label: "新規作成" },
      { href: "/managers/kpi_terms", label: "一覧" },
    ],
    hrefPatterns: [
      "/managers/kpi_terms",
      "/managers/kpi_terms/*",
      "/managers/belonging_employees/kpi_terms",
    ],
  },
  ロールプレイングテンプレ: {
    lists: [
      {
        href: "/managers/roleplaying_condition_templates/new",
        label: "新規作成",
      },
      { href: "/managers/roleplaying_condition_templates", label: "一覧" },
    ],
    hrefPatterns: [
      "/managers/roleplaying_condition_templates",
      "/managers/roleplaying_condition_templates/*",
    ],
  },
  ロールプレイング: {
    lists: [
      { href: "/managers/roleplaying_conditions/new", label: "新規作成" },
      { href: "/managers/roleplaying_conditions", label: "一覧" },
      {
        href: "/managers/roleplaying_condition/employees/dashboard",
        label: "状況確認",
      },
    ],
    hrefPatterns: [
      "/managers/roleplaying_conditions",
      "/managers/roleplaying_conditions/*",
      "/managers/roleplaying_condition/employees/dashboard",
      "/managers/belonging_employees/roleplaying_conditions",
    ],
  },
  "改善・相談": {
    href: "/managers/feedback/kaizen_ideas",
    hrefPatterns: ["/managers/feedback/kaizen_ideas"],
  },
  AIカスタマイズ: {
    href: "/managers/ai_costomize/company_contexts",
    hrefPatterns: ["/managers/ai_costomize/*"],
  },
  ユーザー設定: {
    lists: [
      { href: "/managers/employees/new", label: "新規作成" },
      { href: "/managers/employees", label: "一覧" },
      { href: "/managers/left_employees", label: "無効ユーザー" },
      { href: "/managers/employee_bulk_invite", label: "一括登録" },
    ],
    hrefPatterns: [
      "/managers/employees",
      "/managers/employees/*",
      "/managers/left_employees",
      "/managers/left_employees/*",
      "/managers/employee_bulk_invite",
    ],
  },
  部署設定: {
    lists: [
      { href: "/managers/business_divisions/new", label: "新規作成" },
      { href: "/managers/business_divisions", label: "一覧" },
      { href: "/managers/archived_business_divisions", label: "無効部署" },
    ],
    hrefPatterns: [
      "/managers/business_divisions",
      "/managers/business_divisions/*",
      "/managers/archived_business_divisions",
    ],
  },
  課設定: {
    lists: [
      { href: "/managers/business_sections/new", label: "新規作成" },
      { href: "/managers/business_sections", label: "一覧" },
      { href: "/managers/archived_business_sections", label: "無効課" },
    ],
    hrefPatterns: [
      "/managers/business_sections",
      "/managers/business_sections/*",
      "/managers/archived_business_sections",
    ],
  },
  ジャンル設定: {
    lists: [
      { href: "/managers/providing_services/new", label: "新規作成" },
      { href: "/managers/providing_services", label: "一覧" },
      { href: "/managers/archived_providing_services", label: "無効ジャンル" },
    ],
    hrefPatterns: [
      "/managers/providing_services",
      "/managers/providing_services/*",
      "/managers/archived_providing_services",
    ],
  },
  職種設定: {
    lists: [
      { href: "/managers/job_categories/new", label: "新規作成" },
      { href: "/managers/job_categories", label: "一覧" },
      { href: "/managers/archived_job_categories", label: "無効職種" },
      {
        href: "/managers/archived_job_category_phases",
        label: "無効メモログ項目",
      },
    ],
    hrefPatterns: [
      "/managers/job_categories",
      "/managers/job_categories/*",
      "/managers/archived_job_categories",
      "/managers/archived_job_category_phases",
    ],
  },
  非同期Job実行状況: {
    href: "/managers/background_job_statuses",
    hrefPatterns: ["/managers/background_job_statuses"],
  },
  週次レポート: {
    href: "/managers/weekly_reports",
    hrefPatterns: ["/managers/weekly_reports", "/managers/weekly_reports/*"],
  },
});
